<template>
  <div>
    <div class="operator-cards grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <OperatorCard
        v-for="operator in displayedOperators"
        :key="operator.id"
        :operator="operator"
        @view-on-map="handleViewOnMap(operator)"
      />
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore" class="load-more-container mt-6 text-center">
      <button @click="loadMore" class="load-more-btn btn">Load More</button>
      <!-- Optional: Show count info -->
      <div v-if="operators.length > 12" class="operator-count">
        Showing {{ displayedOperators.length }} of {{ operators.length }} results
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, computed, watch } from "vue";
import OperatorCard from "./OperatorCard.vue";
import { useConfigStore } from "../stores";

const config = useConfigStore();

// Membership taxonomy term IDs (Platinum first, then Gold, then everyone else)
const PLATINUM_ID = 495;
const GOLD_ID = 496;

const props = defineProps({
  operators: {
    type: Array,
    required: true,
  },
  randomize: {
    type: Boolean,
    default: true,
  },
  // Term IDs of the page's ACF primary location; operators here float to the top.
  primaryLocation: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["select-operator", "view-on-map", "toggle-trip-planner"]);

// Pagination state
const itemsPerPage = 12;
const currentPage = ref(1);

// Randomized operators
const randomizedOperators = ref([]);

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Rank operators by membership tier: Platinum (0), Gold (1), everyone else (2)
const membershipRank = (operator) => {
  const ids = operator.membership || [];
  if (ids.includes(PLATINUM_ID)) return 0;
  if (ids.includes(GOLD_ID)) return 1;
  return 2;
};

// Is this operator in the page's primary location?
const isPrimaryLocation = (operator) =>
  props.primaryLocation.length > 0 &&
  props.primaryLocation.some((id) => (operator.location || []).includes(id));

// Composite priority: primary-location is the dominant group, membership orders
// within each block. Rank = (primary?0:1)*3 + membershipRank → 0..5:
//   0 Platinum+primary, 1 Gold+primary, 2 primary, 3 Platinum, 4 Gold, 5 rest.
const priorityRank = (operator) => {
  const primaryPart = isPrimaryLocation(operator) ? 0 : 1;
  const memberPart = config.features.membershipOrdering ? membershipRank(operator) : 0;
  return primaryPart * 3 + memberPart;
};

// Array.prototype.sort is stable (ES2019+), so the random order produced by
// shuffleArray is preserved within each group.
const sortByPriority = (array) => [...array].sort((a, b) => priorityRank(a) - priorityRank(b));

// Computed properties
const displayedOperators = computed(() => {
  const endIndex = currentPage.value * itemsPerPage;
  return randomizedOperators.value.slice(0, endIndex);
});

const hasMore = computed(() => {
  return displayedOperators.value.length < randomizedOperators.value.length;
});

// Methods
const loadMore = () => {
  currentPage.value += 1;
};

const handleViewOnMap = (operator) => {
  emit("view-on-map", operator);
};

// Initialize operators and watch for changes (also re-sort if the primary location arrives late)
watch(
  [() => props.operators, () => props.primaryLocation],
  ([newOperators]) => {
    const ordered = props.randomize ? shuffleArray(newOperators) : [...newOperators];
    const needsOrdering = config.features.membershipOrdering || props.primaryLocation.length > 0;
    randomizedOperators.value = needsOrdering ? sortByPriority(ordered) : ordered;
    currentPage.value = 1;
  },
  { immediate: true, deep: true },
);
</script>

<style scoped>
.operator-cards {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@container app (min-width: 768px) {
  .operator-list-full .operator-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@container app (min-width: 1200px) {
  .operator-cards {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .operator-list-full .operator-cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@container app (min-width: 1440px) {
  .operator-cards {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .operator-list-full .operator-cards {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

.load-more-container {
  margin-top: 1em;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.operator-count {
  font-size: 13px;
  padding: 0.5em;
  color: #6b7280;
}
</style>
